'use client';

import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import {
    ComponentProps,
    MouseEvent,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { markNewsRead } from '@/features/news/mark-news-read';
import { openNewsDetailFromToast } from '@/features/news/open-customer-notification';
import { QueryApi } from '@/features/query/api';
import { getQueryClient } from '@/shared/api/query-client';
import { cn } from '@/shared/lib/cn';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { LocalizedString } from '@/shared/model/Language';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Media as MediaT } from '@/shared/ui/media/types';
import { Modal } from '@/shared/ui/modal';
import { ModalService } from '@/shared/ui/modal/ModalService';
import { ModalProps } from '@/shared/ui/modal/types';
import './news-modal.scss';

type Props = ModalProps & {
    notificationId?: string;
    markReadOnClose?: boolean;
    media?: MediaT;
    mediaGallery?: MediaT[];
    title: LocalizedString;
    content: LocalizedString;
    link?: {
        url: string;
        content: LocalizedString;
    };
    lockScroll?: boolean;
    children?: ReactNode;
};

type NewsModalContextValue = {
    notificationId?: string;
    media?: MediaT;
    mediaGallery?: MediaT[];
    title: LocalizedString;
    content: LocalizedString;
    link?: {
        url: string;
        content: LocalizedString;
    };
    onClose: ModalProps['onClose'];
};

const NewsModalContext = createContext<NewsModalContextValue | null>(null);

const useNewsModalContext = () => {
    const context = useContext(NewsModalContext);
    if (!context) {
        throw new Error('NewsModal components must be used within NewsModal.');
    }
    return context;
};

const NewsModalProvider = ({
    children,
    notificationId,
    markReadOnClose,
    modalId,
    media,
    mediaGallery,
    title,
    content,
    link,
    onClose,
}: Props) => {
    useEffect(() => {
        if (!markReadOnClose || !notificationId || modalId == null) return;
        ModalService.getState().setCloseGuard(modalId, () => {
            const isLoggedIn = Boolean(
                getQueryClient().getQueryData(QueryApi.Customer.queries.Customer.queryKey),
            );
            void markNewsRead({ id: notificationId, isLoggedIn, queryClient: getQueryClient() });
            return true;
        });
    }, [markReadOnClose, notificationId, modalId]);

    return (
        <NewsModalContext.Provider
            value={{
                notificationId,
                media,
                mediaGallery,
                title,
                content,
                link,
                onClose,
            }}
        >
            {children}
        </NewsModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
const NewsModalProviderComponent = NewsModalProvider;

type MediaProps = ComponentProps<typeof Layout> & {
    /** When false (toast), always show single `media` even if gallery has multiple items. */
    carousel?: boolean;
};

const NewsModalMedia = ({ media: mediaProps, carousel = true, ...props }: MediaProps) => {
    const { media, mediaGallery } = useNewsModalContext();
    // Toast (carousel=false) always uses main `media`; detail prefers full gallery.
    const slides = !carousel
        ? media
            ? [media]
            : []
        : mediaGallery && mediaGallery.length > 0
          ? mediaGallery
          : media
            ? [media]
            : [];
    const isScrollable = carousel && slides.length > 1;

    const [emblaRef, emblaApi] = useEmblaCarousel({
        active: isScrollable,
        loop: false,
        align: 'start',
    });
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const handleSlideButtonClick = useCallback(
        (index: number) => {
            if (!emblaApi) return;
            emblaApi.scrollTo(index);
        },
        [emblaApi],
    );
    const onInit = useCallback((api: EmblaCarouselType) => {
        setScrollSnaps(api.scrollSnapList());
    }, []);
    const onSelect = useCallback((api: EmblaCarouselType) => {
        setSelectedIndex(api.selectedScrollSnap());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;
        const frame = requestAnimationFrame(() => {
            onInit(emblaApi);
            onSelect(emblaApi);
        });
        emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect);
        return () => {
            cancelAnimationFrame(frame);
            emblaApi.off('reInit', onInit).off('reInit', onSelect).off('select', onSelect);
        };
    }, [emblaApi, onInit, onSelect]);

    if (slides.length === 0) {
        return null;
    }

    if (!isScrollable) {
        return (
            <Layout
                media={mediaProps}
                data-testid="news-modal-media-wrapper"
                data-news-modal-media=""
                {...props}
            >
                <Media data={slides[0]} data-testid="news-modal-media" />
            </Layout>
        );
    }

    return (
        <Layout
            media={mediaProps}
            data-testid="news-modal-media-wrapper"
            data-news-modal-media=""
            data-carousel=""
            {...props}
        >
            <div className="news-modal__image-carousel" data-testid="news-modal-image-carousel">
                <div
                    ref={emblaRef}
                    className="news-modal__image-viewport"
                    data-testid="news-modal-image-viewport"
                >
                    <div className="news-modal__image-container">
                        {slides.map((slide, index) => (
                            <div
                                key={`${slide.url}-${index}`}
                                className="news-modal__image-slide"
                                data-testid="news-modal-image-slide"
                                data-slide={index + 1}
                            >
                                <Media data={slide} data-testid="news-modal-media" data-value={slide.url} />
                            </div>
                        ))}
                    </div>
                    <div className="news-modal__image-controls" data-testid="news-modal-image-controls">
                        <div className="news-modal__image-dots" data-testid="news-modal-image-dots">
                            {scrollSnaps.map((_, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className={cn(
                                        'news-modal__image-dot',
                                        index === selectedIndex && 'news-modal__image-dot--active',
                                    )}
                                    onClick={() => handleSlideButtonClick(index)}
                                    aria-label={`Go to slide ${index + 1}`}
                                    data-testid="news-modal-image-dot"
                                    data-slide={index + 1}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

type TitleProps = ComponentProps<typeof Modal.Title>;
const NewsModalTitle = ({ media: mediaProps, ...props }: TitleProps) => {
    const { title } = useNewsModalContext();
    const getLocalizedValue = useGetLocalizedValue();

    return (
        <Modal.Title media={mediaProps} data-testid="news-modal-title" {...props}>
            {getLocalizedValue(title)}
        </Modal.Title>
    );
};

type DescriptionProps = ComponentProps<typeof Modal.Description>;
const NewsModalDescription = ({ media: mediaProps, ...props }: DescriptionProps) => {
    const { content } = useNewsModalContext();
    const getLocalizedValue = useGetLocalizedValue();

    return (
        <Modal.Description media={mediaProps} data-testid="news-modal-description" {...props}>
            {getLocalizedValue(content)}
        </Modal.Description>
    );
};

type BadgeProps = ComponentProps<typeof Layout>;
const NewsModalBadge = ({ media: mediaProps, ...props }: BadgeProps) => {
    const t = useTranslations();

    return (
        <Layout media={mediaProps} {...props}>
            <Badge variant="success" data-testid="news-modal-badge">
                {t('NEWS_MODAL.BADGE')}
            </Badge>
        </Layout>
    );
};

/** Renders CTA button content. Use inside NewsModal.FixedBottomPanel. */
type BottomPanelProps = ComponentProps<typeof Layout>;
const NewsModalBottomPanel = ({ media: mediaProps, ...props }: BottomPanelProps) => {
    const { link, onClose } = useNewsModalContext();
    const getLocalizedValue = useGetLocalizedValue();

    if (!link) {
        return null;
    }

    return (
        <Layout element={Button} media={mediaProps} asChild data-testid="news-modal-cta-wrapper" {...props}>
            <Link href={link.url} onClick={() => onClose('close-btn')} data-testid="news-modal-cta">
                {getLocalizedValue(link.content)}
            </Link>
        </Layout>
    );
};

type FixedBottomPanelProps = ComponentProps<typeof Modal.FixedBottomPanel>;
const NewsModalFixedBottomPanel = ({
    className,
    media: mediaProps,
    children,
    ...props
}: FixedBottomPanelProps) => {
    const { link } = useNewsModalContext();

    if (!link) {
        return null;
    }

    return (
        <Modal.FixedBottomPanel className={className} media={mediaProps} {...props}>
            {children ?? <NewsModalBottomPanel />}
        </Modal.FixedBottomPanel>
    );
};

/** Toast content click target: closes toast and opens NewsDetail with the same props. */
type OpenDetailProps = ComponentProps<typeof Layout>;
const NewsModalOpenDetail = ({ children, onClick, ...props }: OpenDetailProps) => {
    const { notificationId, media, mediaGallery, title, content, link, onClose } = useNewsModalContext();

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);
        if (!notificationId) return;
        void openNewsDetailFromToast(onClose, {
            notificationId,
            media,
            mediaGallery,
            title,
            content,
            link,
        });
    };

    return (
        <Layout
            element="button"
            type="button"
            data-testid="news-modal-open-detail"
            {...props}
            onClick={handleClick}
        >
            {children}
        </Layout>
    );
};

export const NewsModalWrapper = ({
    children,
    lockScroll = true,
    notificationId,
    markReadOnClose,
    onClose,
    ...props
}: Props) => {
    return (
        <NewsModalProvider
            {...props}
            notificationId={notificationId}
            markReadOnClose={markReadOnClose}
            lockScroll={lockScroll}
            onClose={onClose}
        >
            <Modal
                {...props}
                onClose={onClose}
                lockScroll={lockScroll}
                wrapperProps={{
                    'data-testid': 'news-modal',
                }}
            >
                {children}
            </Modal>
        </NewsModalProvider>
    );
};

export {
    NewsModalWrapper as Wrapper,
    NewsModalProviderComponent as Provider,
    NewsModalMedia as Media,
    NewsModalTitle as Title,
    NewsModalDescription as Description,
    NewsModalBadge as Badge,
    NewsModalBottomPanel as BottomPanel,
    NewsModalFixedBottomPanel as FixedBottomPanel,
    NewsModalOpenDetail as OpenDetail,
};

export { NewsModalWrapper as NewsModal };
